import { useState } from 'react';
import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const INITIAL_SHOW = 12;

const niches = [
  { icon: 'ri-building-2-line', name: 'Строительство и ремонт', tag: 'Стройка' },
  { icon: 'ri-scales-3-line', name: 'Юридические услуги', tag: 'Юристы' },
  { icon: 'ri-heart-pulse-line', name: 'Медицинские услуги и клиники', tag: 'Медицина' },
  { icon: 'ri-graduation-cap-line', name: 'Образование и онлайн-курсы', tag: 'EdTech' },
  { icon: 'ri-code-s-slash-line', name: 'IT-разработка и аутсорсинг', tag: 'IT' },
  { icon: 'ri-megaphone-line', name: 'Маркетинг и реклама', tag: 'Маркетинг' },
  { icon: 'ri-search-eye-line', name: 'SEO-продвижение', tag: 'SEO' },
  { icon: 'ri-advertisement-line', name: 'Контекстная реклама', tag: 'PPC' },
  { icon: 'ri-smartphone-line', name: 'SMM и таргетированная реклама', tag: 'SMM' },
  { icon: 'ri-window-line', name: 'Веб-разработка и создание сайтов', tag: 'Web' },
  { icon: 'ri-palette-line', name: 'Дизайн и брендинг', tag: 'Дизайн' },
  { icon: 'ri-bank-line', name: 'Финансовые и страховые услуги', tag: 'Финансы' },
  { icon: 'ri-calculator-line', name: 'Бухгалтерские услуги', tag: 'Бухгалтерия' },
  { icon: 'ri-truck-line', name: 'Логистика и грузоперевозки', tag: 'Логистика' },
  { icon: 'ri-home-3-line', name: 'Агентства недвижимости', tag: 'Недвижимость' },
  { icon: 'ri-user-search-line', name: 'Рекрутинг и кадровые агентства', tag: 'HR' },
  { icon: 'ri-stack-line', name: 'Производство и промышленность', tag: 'Производство' },
  { icon: 'ri-shopping-bag-3-line', name: 'Оптовая и розничная торговля', tag: 'Торговля' },
  { icon: 'ri-calendar-event-line', name: 'Event-агентства и организация мероприятий', tag: 'Events' },
  { icon: 'ri-home-wifi-line', name: 'Умный дом и системы безопасности', tag: 'Безопасность' },
  { icon: 'ri-brush-2-line', name: 'Клининг и бытовые услуги', tag: 'Клининг' },
  { icon: 'ri-tools-line', name: 'Ремонт техники и сервисные центры', tag: 'Сервис' },
  { icon: 'ri-car-line', name: 'Автосервис и автоподбор', tag: 'Авто' },
  { icon: 'ri-camera-lens-line', name: 'Фото- и видеосъёмка', tag: 'Медиа' },
  { icon: 'ri-team-line', name: 'Консалтинг и бизнес-услуги', tag: 'Консалтинг' },
  { icon: 'ri-cup-line', name: 'Рестораны и доставка еды', tag: 'HoReCa' },
  { icon: 'ri-flight-takeoff-line', name: 'Туризм и тревел-агентства', tag: 'Туризм' },
  { icon: 'ri-plant-line', name: 'Фитнес и спортивные клубы', tag: 'Фитнес' },
  { icon: 'ri-brain-line', name: 'Психология и коучинг', tag: 'Коучинг' },
  { icon: 'ri-hand-heart-line', name: 'Благотворительные организации', tag: 'НКО' },
  { icon: 'ri-building-4-line', name: 'Мебель и интерьер', tag: 'Интерьер' },
  { icon: 'ri-android-line', name: 'Мобильная разработка', tag: 'Mobile' },
  { icon: 'ri-shield-check-line', name: 'Информационная безопасность', tag: 'ИБ' },
  { icon: 'ri-database-2-line', name: 'Big Data и аналитика', tag: 'Данные' },
  { icon: 'ri-government-line', name: 'Госзакупки и тендеры', tag: 'Тендеры' },
  { icon: 'ri-police-car-line', name: 'Охранные предприятия', tag: 'Охрана' },
];

export default function NichesSection() {
  const [showAll, setShowAll] = useState(false);
  const visibleNiches = showAll ? niches : niches.slice(0, INITIAL_SHOW);
  const hiddenCount = niches.length - INITIAL_SHOW;

  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 100 });
  const { ref: descRef, revealed: descRevealed } = useScrollReveal({ delay: 200 });
  const { containerRef: gridRef, revealedCount: gridRevealed } = useScrollRevealGroup<HTMLDivElement>(INITIAL_SHOW);

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-primary-500/3 blur-3xl pointer-events-none"></div>

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="text-center mb-12 md:mb-16">
          <span
            ref={labelRef}
            className={`inline-block text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}
          >
            Ниши
          </span>
          <h2
            ref={titleRef}
            className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}
          >
            С кем мы работаем
          </h2>
          <p
            ref={descRef}
            className={`mt-4 text-[17px] text-foreground-500 leading-relaxed max-w-2xl mx-auto reveal-fade-up ${descRevealed ? 'revealed' : ''}`}
          >
            AIGAM находит клиентов в любых нишах — от локальных услуг до сложных B2B-продуктов. Система адаптируется под специфику вашего бизнеса, язык аудитории и цикл сделки.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {visibleNiches.map((niche, idx) => (
            <div
              key={niche.name}
              className={`group rounded-xl border border-background-200 bg-background-50 hover:border-primary-400 hover:bg-primary-50/30 transition-all duration-300 p-4 md:p-5 flex flex-col items-center text-center cursor-default reveal-fade-up ${idx < gridRevealed ? 'revealed' : ''}`}
              style={idx < gridRevealed ? { transitionDelay: `${idx * 30}ms` } : undefined}
            >
              <span className="w-10 h-10 rounded-lg bg-background-100 text-foreground-600 group-hover:bg-primary-100 group-hover:text-primary-500 flex items-center justify-center transition-colors mb-3">
                <i className={`${niche.icon} text-[18px]`}></i>
              </span>
              <span className="text-[13px] font-medium text-foreground-700 group-hover:text-foreground-950 leading-snug transition-colors">
                {niche.name}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-200 hover:border-primary-400 hover:bg-primary-50/30 text-[14px] font-medium text-foreground-700 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            {showAll ? (
              <>
                Свернуть
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-up-s-line"></i>
                </span>
              </>
            ) : (
              <>
                Показать все {hiddenCount} ниш
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}